import { type DocumentVersionSummary } from "~/api/types";

export interface DocumentVersionSummaryChart extends DocumentVersionSummary {
  added: boolean;
}

interface DocumentLink {
  parentDocumentId: number;
  childDocumentId: number;
}

const sortVersion = (a: DocumentVersionSummary, b: DocumentVersionSummary) => {
  const aOrder = a.document.chartOrder;
  const bOrder = b.document.chartOrder;

  if (aOrder !== null && bOrder !== null) {
    return aOrder - bOrder;
  }

  // Documents with a chartOrder come before those without
  if (aOrder !== null) return -1;
  if (bOrder !== null) return 1;

  return a.title.localeCompare(b.title);
};

type DocumentId = number;
export type DocumentVersionMap = Map<DocumentId, DocumentVersionSummaryChart>;
export type CategoryColumnMap = Map<number, DocumentVersionSummaryChart[]>;
export type CategoryMap = Map<number, CategoryColumnMap[]>;

export const getChartData = (publishedVersions: DocumentVersionSummary[]) => {
  const { documentVersionMap, links } = extractData(publishedVersions);

  // Filter relationship arrays to only include documents visible on the chart.
  documentVersionMap.forEach((version) => {
    version.document = {
      ...version.document,
      parentDocumentIds: version.document.parentDocumentIds.filter((id) =>
        documentVersionMap.has(id),
      ),
      childDocumentIds: version.document.childDocumentIds.filter((id) =>
        documentVersionMap.has(id),
      ),
    };
  });

  const graphs = createGraphs(documentVersionMap, links);
  const categoryMap = getCategoryMap(documentVersionMap, graphs);

  return { categoryMap, documentVersionMap };
};

// Prepare the data for the chart
const getCategoryMap = (
  documentVersionMap: DocumentVersionMap,
  graphs: Graph[],
) => {
  const categories = new Map<number, CategoryColumnMap[]>();

  // Add all connected documents
  graphs.forEach((graph) => {
    const categoryColumn = new Map<number, DocumentVersionSummaryChart[]>();

    // Find root documents and recursively add all child documents
    graph.documentVersions
      .filter((v) => v.document.parentDocumentIds.length === 0)
      .forEach((v) => {
        recursivelyAddDocument(v, categoryColumn, documentVersionMap);
      });

    const tierMaps = [
      ...(categories.get(graph.categoryId) ?? []),
      categoryColumn,
    ];

    categories.set(graph.categoryId, tierMaps);
  });

  // Add all documents that are not connected
  const unconnectedMap = new Map<number, CategoryColumnMap>();

  documentVersionMap.forEach((version) => {
    if (!version.added) {
      const tierMap =
        unconnectedMap.get(version.document.category.id) ??
        new Map<number, DocumentVersionSummaryChart[]>();

      const tierDocuments = [
        ...(tierMap.get(version.document.tier.id) ?? []),
        version,
      ];

      tierMap.set(version.document.tier.id, tierDocuments);
      unconnectedMap.set(version.document.category.id, tierMap);
    }
  });

  // Add unconnected documents to the category map
  unconnectedMap.forEach((tierMap, categoryId) => {
    const existingTierMaps = categories.get(categoryId) ?? [];
    const tierMaps = [...existingTierMaps, tierMap];
    categories.set(categoryId, tierMaps);
  });

  // Sort the documents in each tier alphabetically
  categories.forEach((tierMaps) => {
    tierMaps.forEach((tierMap) => {
      tierMap.forEach((documents) => documents.sort(sortVersion));
    });
  });

  return categories;
};

const recursivelyAddDocument = (
  version: DocumentVersionSummaryChart,
  categoryColumn: CategoryColumnMap,
  documents: DocumentVersionMap,
) => {
  version.added = true;
  const tierDocuments = [
    ...(categoryColumn.get(version.document.tier.id) ?? []),
    version,
  ];
  categoryColumn.set(version.document.tier.id, tierDocuments);

  version.document.childDocumentIds.forEach((childId) => {
    const childDocument = documents.get(childId);
    // If a child document has no published version, we don't want to add it to the chart
    if (childDocument && !childDocument.added) {
      recursivelyAddDocument(childDocument, categoryColumn, documents);
    }
  });
};

interface Graph {
  id: number;
  categoryId: number;
  documentVersions: DocumentVersionSummaryChart[];
}

// Find all connected documents
const createGraphs = (
  documentVersionMap: DocumentVersionMap,
  links: DocumentLink[],
) => {
  let graphs: Graph[] = [];

  let counter = 0;

  for (const { parentDocumentId, childDocumentId } of links) {
    const parentVersion = documentVersionMap.get(parentDocumentId);
    const childVersion = documentVersionMap.get(childDocumentId);

    if (!parentVersion || !childVersion) {
      // this could happen if a link was created for a draft document
      continue;
    }

    const parentGraph = graphs.find((sg) =>
      sg.documentVersions.find((n) => n.document.id === parentDocumentId),
    );
    const childGraph = graphs.find((sg) =>
      sg.documentVersions.find((n) => n.document.id === childDocumentId),
    );

    if (!parentGraph && !childGraph) {
      // If both documents are not in a graph, create a new graph
      graphs.push({
        id: counter,
        categoryId: parentVersion.document.category.id,
        documentVersions: [parentVersion, childVersion],
      });
      counter += 1;
    } else if (parentGraph && !childGraph) {
      // If fromDocument is in a graph, add toDocument to the same graph
      parentGraph.documentVersions.push(childVersion);
    } else if (!parentGraph && childGraph) {
      // If toDocument is in a graph, add fromDocument to the same graph
      childGraph.documentVersions.push(parentVersion);
    } else if (parentGraph && childGraph && parentGraph.id !== childGraph.id) {
      // If both documents are in different graphs, merge the graphs
      parentGraph.documentVersions.push(...childGraph.documentVersions);
      graphs = graphs.filter((sg) => sg.id !== childGraph.id);
    }
  }

  return graphs;
};

// Add all documents to a map and extract all links between documents for easy access
const extractData = (versions: DocumentVersionSummary[]) => {
  const documentVersionMap = new Map<DocumentId, DocumentVersionSummaryChart>();
  const links: DocumentLink[] = [];

  versions.forEach((d) => {
    documentVersionMap.set(d.document.id, {
      ...d,
      added: false,
    });

    d.document.childDocumentIds.forEach((childId) => {
      links.push({
        parentDocumentId: d.document.id,
        childDocumentId: childId,
      });
    });
  });

  links.sort((a, b) => a.parentDocumentId - b.parentDocumentId);

  return { documentVersionMap, links };
};
